import React, { useEffect, useState } from 'react';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import { IoClose } from 'react-icons/io5';
import { ActionIcon, Button, cn, Modal, Text } from 'rizzui';
import { CustomBadge } from '../../../../../components/forms/CustomBadge';
import Icon from '../../../../../components/icon/Icon';
import { UserService } from '../../../../../services/user/userService';
import { getPositionBadgeColor, getPositionIcon } from './RankingCard';

interface RankingUser {
  id: string;
  name: string;
  lastName: string;
  email: string;
  weeklyHours: string;
  totalPoints: string;
  position: number;
}

interface RankingClassificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  rankingId: string;
  rankingTitle: string;
  currentUserId?: string;
}

/* ------------------------ COMPONENTE USER ITEM ------------------------ */

const UserRankingItem: React.FC<{
  user: RankingUser;
  isCurrentUser: boolean;
}> = ({ user, isCurrentUser }) => {

  return (
    <div
      className={cn(
        "flex flex-col p-4 rounded-lg border transition-all duration-200 relative",
        isCurrentUser
          ? "bg-primary-50 border-primary-200 shadow-md"
          : "bg-white border-gray-200 hover:bg-gray-50"
      )}
    >

      {/* CABECERA */}
      <div className="flex items-center justify-between">

        {/* Información del usuario */}
        <div className="flex items-center space-x-4">
          <CustomBadge
            customColor={getPositionBadgeColor(user.position)}
            variant="solid"
            size="lg"
            className="min-w-[40px] h-8 flex items-center justify-center font-bold"
          >
            {user.position}º
          </CustomBadge>

          {getPositionIcon(user.position) && (
            <Icon
              icon={getPositionIcon(user.position)!}
              className="w-5 h-5 text-yellow-500"
            />
          )}

          <div className="flex flex-col">
            <Text className="font-semibold text-sm">
              {user.name} {user.lastName}
            </Text>
            <Text className="text-xs text-gray-500">{user.email}</Text>
          </div>
        </div>

        {/* DERECHA → PUNTOS + COMISIÓN (tooltip estilo ranking) */}
        <div className="text-right relative">

          <Text className="text-sm font-bold text-green-600">
            {user.totalPoints} pts
          </Text>

          <Text className="text-xs text-gray-500">{user.weeklyHours}h/sem</Text>
        </div>
      </div>
    </div>
  );
};

/* --------------------------- MODAL COMPLETO --------------------------- */

const RankingClassificationModal: React.FC<RankingClassificationModalProps> = ({
  isOpen,
  onClose,
  rankingId,
  rankingTitle,
  currentUserId
}) => {

  const userService = new UserService();

  const [activeModel, setActiveModel] = useState("2026"); // valor por defecto
  const [users, setUsers] = useState<RankingUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const maxUsersToShow = 10;
  const displayedUsers = showAll ? users : users.slice(0, maxUsersToShow);
  const hasMoreUsers = users.length > maxUsersToShow;

  /* --- OBTENER CONFIGURACIÓN DEL MODELO --- */
  const fetchActiveModel = async () => {
    try {
      const response = await userService.getCompanyConfigurationInfo();
      const data = response.getResponseData()?.data;

      if (data?.activeContractCommissionModel) {
        setActiveModel(data.activeContractCommissionModel);
      }
    } catch (e) {
      console.error("Error cargando modelo de comisiones", e);
    }
  };

  /* --- OBTENER CLASIFICACIÓN --- */
  const fetchRankingClassification = async () => {
    if (!rankingId) return;

    setLoading(true);
    try {
      const response = await userService.showRankingClassification(rankingId);
      const result = response.getResponseData();

      if (result.success) {
        setUsers(result.data.sort((a: RankingUser, b: RankingUser) => a.position - b.position));
      } else {
        setError("Error al cargar clasificación");
      }
    } catch {
      setError("Error al cargar clasificación");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    fetchActiveModel();          // ← cargar modelo desde servicio
    fetchRankingClassification(); // ← cargar usuarios

  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="m-auto px-6 pt-6 pb-8 flex flex-col max-h-[90vh]">

        {/* HEADER */}
        <div className="mb-6 flex items-center justify-between">
          <Text className="text-xl font-semibold">Clasificación: {rankingTitle}</Text>
          <ActionIcon size="sm" variant="text" onClick={onClose}>
            <IoClose className="w-6" />
          </ActionIcon>
        </div>

        {/* LISTADO */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <Text>Cargando...</Text>
          ) : error ? (
            <Text className="text-red-500">{error}</Text>
          ) : (
            <div className="space-y-3">
              {displayedUsers.map(user => (
                <UserRankingItem
                  key={user.id}
                  user={user}
                  isCurrentUser={user.id === currentUserId}
                />
              ))}
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
};

export default RankingClassificationModal;
